import React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  /** Title text for screen readers when no visible title. */
  ariaLabel?: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  hideClose?: boolean
  /** When false, backdrop click and Esc key do not close (only the X button does). */
  dismissable?: boolean
}

const sizeClasses: Record<string, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-7xl',
}

/**
 * App-wide modal component built on Radix Dialog.
 * Use this for ALL modals — no raw `.modal` divs.
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  ariaLabel,
  children,
  size = 'md',
  hideClose,
  dismissable = true,
}: ModalProps) => {
  return (
    <Dialog.Root open={isOpen} onOpenChange={open => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 animate-fade-in" />
        <Dialog.Content
          onPointerDownOutside={e => {
            if (!dismissable) {
              e.preventDefault()
              return
            }
            const target = e.target as HTMLElement
            if (target.closest('[role="dialog"]')) {
              e.preventDefault()
            }
          }}
          onEscapeKeyDown={e => {
            if (!dismissable) {
              e.preventDefault()
            }
          }}
          className={`fixed z-50 inset-0 m-auto ${sizeClasses[size]} w-[calc(100%-2rem)] h-fit
            max-h-[calc(100dvh-2rem)] flex flex-col rounded-2xl shadow-xl animate-fade-in
            focus:outline-hidden overflow-hidden`}
          style={{
            backgroundColor: 'var(--content-background-color, #fff)',
            border: '1px solid var(--border-color, #e5e7eb)',
            color: 'var(--text-color)',
          }}>
          {/* Radix requires Dialog.Title for screen readers. When the modal has no
              visible title we still render one but visually hide it via `sr-only`. */}
          {!title && <Dialog.Title className="sr-only">{ariaLabel || 'Dialog'}</Dialog.Title>}
          {title && (
            <div
              className="flex items-center justify-between px-5 py-3"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--theme-color) 6%, var(--content-background-color))',
              }}>
              <Dialog.Title className="text-lg font-semibold" style={{ color: 'var(--text-color)' }}>
                {title}
              </Dialog.Title>
              {!hideClose && (
                <Dialog.Close asChild>
                  <button
                    className="modal-close-btn w-9 h-9 flex items-center justify-center rounded-full transition-all active:scale-90"
                    style={{ minWidth: 0, minHeight: 0, border: 'none' }}
                    aria-label="Close">
                    <X size={18} strokeWidth={2.25} />
                  </button>
                </Dialog.Close>
              )}
            </div>
          )}
          {!title && !hideClose && (
            <Dialog.Close asChild>
              <button
                className="modal-close-btn absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full transition-all active:scale-90"
                style={{ minWidth: 0, minHeight: 0, border: 'none' }}
                aria-label="Close">
                <X size={18} strokeWidth={2.25} />
              </button>
            </Dialog.Close>
          )}
          <Dialog.Description asChild>
            <div className="p-6 overflow-y-auto flex-1 min-h-0">{children}</div>
          </Dialog.Description>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default Modal
